import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const LAYERS = [
  { key: "display", label: "OLED-Display", color: "linear-gradient(145deg,#111,#000)", accent: "#4da3ff" },
  { key: "frame", label: "Titan-Rahmen", color: "linear-gradient(145deg,#3a3a3d,#1a1a1d)", accent: "#c9c9cf" },
  { key: "battery", label: "Akku", color: "linear-gradient(145deg,#123b2a,#0a1f16)", accent: "#37d67a" },
  { key: "board", label: "Logic Board", color: "linear-gradient(145deg,#2a2140,#140f22)", accent: "#b388ff" },
  { key: "back", label: "Backglass", color: "linear-gradient(145deg,#222,#050505)", accent: "#888" },
];

function Layer({ i, progress }) {
  const n = LAYERS.length;
  const mid = (n - 1) / 2;
  const spread = (i - mid) * 190;
  const z = useTransform(progress, [0, 1], [i * 2, spread]);
  const labelOpacity = useTransform(progress, [0.45, 0.8], [0, 1]);
  const labelX = useTransform(progress, [0.45, 0.9], [-10, 0]);
  const L = LAYERS[i];
  return (
    <motion.div
      style={{ z, transformStyle: "preserve-3d", willChange: "transform" }}
      className="absolute inset-0 flex items-center justify-center"
    >
      <div
        className="relative rounded-[2.4rem]"
        style={{
          width: 210, height: 430, background: L.color,
          boxShadow: `0 30px 60px rgba(0,0,0,0.55), inset 0 0 1px ${L.accent}`,
          border: `1px solid rgba(255,255,255,0.08)`,
        }}
      >
        {L.key === "display" && (
          <div className="absolute inset-2 rounded-[2rem] overflow-hidden">
            <div className="absolute top-3 left-1/2 -translate-x-1/2 w-16 h-5 rounded-full bg-black z-10" />
            <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 60% 30%, rgba(77,163,255,0.25), transparent 60%)" }} />
          </div>
        )}
        {L.key === "battery" && <div className="absolute inset-6 rounded-2xl border border-emerald-400/30" />}
        {L.key === "board" && (
          <div className="absolute inset-5 rounded-2xl border border-purple-300/20 grid grid-cols-3 gap-1 p-2">
            {Array.from({ length: 9 }).map((_, k) => <div key={k} className="rounded bg-white/5" />)}
          </div>
        )}
        {/* label */}
        <motion.div style={{ opacity: labelOpacity, x: labelX }} className="absolute top-1/2 -translate-y-1/2 -right-[150px] flex items-center gap-2">
          <span className="w-10 h-px" style={{ background: L.accent }} />
          <span className="font-mono text-xs uppercase tracking-widest whitespace-nowrap" style={{ color: L.accent }}>{L.label}</span>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default function ExplodedPhone() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  // active spreading window
  const p = useTransform(scrollYProgress, [0.15, 0.6], [0, 1], { clamp: true });
  const rotZ = useTransform(scrollYProgress, [0, 1], [-28, -12]);
  const headOpacity = useTransform(scrollYProgress, [0.15, 0.35], [0, 1]);

  return (
    <section ref={ref} data-testid="exploded" className="relative z-10 bg-[#050505] h-[260vh]">
      <div className="sticky top-0 h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(255,255,255,0.06),transparent_55%)]" />
        <div className="max-w-7xl mx-auto w-full px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative">
          <motion.div style={{ opacity: headOpacity }}>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#888] mb-4">Bis ins kleinste Teil</p>
            <h2 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              Wir kennen<br />jedes Bauteil.
            </h2>
            <p className="text-[#999] mt-6 max-w-md leading-relaxed">
              Vom OLED-Display bis zum Akku – scrolle und sieh, wie sich dein iPhone öffnet.
              Präzision auf Komponenten-Ebene, mit Premium-Ersatzteilen und Sorgfalt.
            </p>
          </motion.div>

          <div className="relative h-[560px]" style={{ perspective: 1400 }}>
            <motion.div
              style={{ rotateX: 58, rotateZ: rotZ, transformStyle: "preserve-3d", willChange: "transform" }}
              className="absolute inset-0"
            >
              {LAYERS.map((_, i) => <Layer key={i} i={i} progress={p} />)}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
